import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const CodeBlock = ({ code, language }) => (
  <div className="relative group">
    <button
      onClick={() => { navigator.clipboard.writeText(code); toast.success('Copied'); }}
      className="absolute top-3 right-3 px-2 py-1 bg-slate-700 text-slate-300 text-xs hover:bg-slate-600 transition-colors opacity-0 group-hover:opacity-100 flex items-center gap-1"
    >
      <Copy size={12} weight="bold" />
      Copy
    </button>
    <pre className="bg-slate-950 text-slate-300 p-5 text-sm font-mono overflow-x-auto leading-relaxed whitespace-pre">
      {code}
    </pre>
  </div>
);

const DOMAIN = BACKEND_URL || 'https://yourdomain.com';

const ApiDocs = () => {
  const [activeTab, setActiveTab] = useState('weather');

  const endpoints = {
    weather: {
      method: 'GET',
      path: '/api/v1/weather',
      description: 'Retrieve historical weather data for a specific location, date, and time.',
      params: [
        { name: 'location', type: 'string', required: true, desc: 'City, address, or ZIP code' },
        { name: 'date', type: 'string', required: true, desc: 'Date in YYYY-MM-DD format' },
        { name: 'time', type: 'string', required: true, desc: 'Time in HH:MM format (24h)' },
      ],
      curl: `curl -H "Authorization: Bearer YOUR_API_KEY" \\
  "${DOMAIN}/api/v1/weather?location=Chicago,IL&date=2024-03-15&time=14:00"`,
      response: `{
  "status": "success",
  "data": {
    "location": {
      "query": "Chicago,IL",
      "formatted_address": "Chicago, IL",
      "latitude": 41.8781,
      "longitude": -87.6298
    },
    "date": "2024-03-15",
    "time": "14:00",
    "weather": {
      "temp": 45.2,
      "humidity": 62.0,
      "windspeed": 15.3,
      "winddir": 270,
      "pressure": 1018.5,
      "cloudcover": 55.0,
      "conditions": "Partly cloudy",
      "precipprob": 10.0
    },
    "metadata": {
      "provider": "Visual Crossing Weather",
      "timezone": "America/Chicago",
      "observation_time": "2024-03-15 14:00:00",
      "data_quality": "live",
      "stations": ["KORD", "KMDW"]
    }
  },
  "rate_limit": {
    "limit": 50,
    "remaining": 49,
    "plan": "free"
  }
}`,
    },
    report: {
      method: 'POST',
      path: '/api/v1/report',
      description: 'Generate a timestamped PDF weather report. Returns the report data plus a base64-encoded PDF. Only available when live data is connected.',
      params: [
        { name: 'location', type: 'string', required: true, desc: 'City, address, or ZIP code' },
        { name: 'date', type: 'string', required: true, desc: 'Date in YYYY-MM-DD format' },
        { name: 'time', type: 'string', required: true, desc: 'Time in HH:MM format (24h)' },
      ],
      curl: `curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"location":"Chicago,IL","date":"2024-03-15","time":"14:00"}' \\
  "${DOMAIN}/api/v1/report"`,
      response: `{
  "status": "success",
  "report_id": "WP-API-A1B2C3D4",
  "data": { ... },
  "pdf_base64": "JVBERi0xLjQgMSAwIG9iai...",
  "rate_limit": { "limit": 50, "remaining": 48, "plan": "free" }
}`,
    },
    bulk: {
      method: 'POST',
      path: '/api/v1/bulk',
      description: 'Look up weather for multiple locations and dates in a single request. Maximum 10 lookups per batch.',
      params: [
        { name: 'lookups', type: 'array', required: true, desc: 'Array of {location, date, time} objects (max 10)' },
      ],
      curl: `curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"lookups":[{"location":"NYC","date":"2024-01-15","time":"12:00"},{"location":"LA","date":"2024-06-01","time":"15:00"}]}' \\
  "${DOMAIN}/api/v1/bulk"`,
      response: `{
  "status": "success",
  "total": 2,
  "successful": 2,
  "results": [
    { "location": {...}, "weather": {...}, "metadata": {...} },
    { "location": {...}, "weather": {...}, "metadata": {...} }
  ],
  "rate_limit": { "limit": 50, "remaining": 47, "plan": "free" }
}`,
    },
  };

  const active = endpoints[activeTab];

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CloudRain size={32} weight="bold" className="text-blue-600" />
              <span className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/developer" data-testid="api-dashboard-link" className="text-sm text-blue-600 hover:text-blue-700 font-medium">API Dashboard</Link>
              <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-2">
                <ArrowLeft size={16} weight="bold" />
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>API Documentation</h1>
          <p className="text-sm text-slate-600 mt-2">Integrate historical weather data into your applications</p>
        </div>

        {/* Authentication */}
        <div className="mb-12 bg-slate-50 border border-slate-200 p-6" data-testid="auth-section">
          <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Authentication</h2>
          <p className="text-sm text-slate-700 mb-4">
            All API requests require an API key passed in the <code className="px-1.5 py-0.5 bg-slate-200 text-slate-900 font-mono text-xs">Authorization</code> header.
          </p>
          <CodeBlock code={`Authorization: Bearer YOUR_API_KEY`} />
          <p className="text-xs text-slate-500 mt-3">
            Generate your API key from the <Link to="/developer" className="text-blue-600 hover:text-blue-700 font-medium">API Dashboard</Link>.
          </p>
        </div>

        {/* Rate Limits */}
        <div className="mb-12 bg-white border border-slate-200 p-6" data-testid="rate-limits-section">
          <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Rate Limits</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 border border-slate-200">
              <p className="text-sm font-semibold text-slate-950 mb-1">Free</p>
              <p className="text-2xl font-mono font-bold text-slate-950">50 <span className="text-xs text-slate-500 font-normal">req/day</span></p>
            </div>
            <div className="p-4 border border-blue-200 bg-blue-50">
              <p className="text-sm font-semibold text-blue-900 mb-1">Starter <span className="text-xs font-normal text-blue-600">$29/mo</span></p>
              <p className="text-2xl font-mono font-bold text-blue-900">1,000 <span className="text-xs text-blue-700 font-normal">req/day</span></p>
            </div>
            <div className="p-4 border border-violet-200 bg-violet-50">
              <p className="text-sm font-semibold text-violet-900 mb-1">Pro <span className="text-xs font-normal text-violet-600">$79/mo</span></p>
              <p className="text-2xl font-mono font-bold text-violet-900">5,000 <span className="text-xs text-violet-700 font-normal">req/day</span></p>
            </div>
            <div className="p-4 border border-amber-200 bg-amber-50">
              <p className="text-sm font-semibold text-amber-900 mb-1">Business <span className="text-xs font-normal text-amber-600">$149/mo</span></p>
              <p className="text-2xl font-mono font-bold text-amber-900">10,000 <span className="text-xs text-amber-700 font-normal">req/day</span></p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Rate limit info is included in every response under <code className="px-1 py-0.5 bg-slate-100 font-mono text-xs">rate_limit</code>. Exceeding the limit returns HTTP 429. The <code className="px-1 py-0.5 bg-slate-100 font-mono text-xs">/report</code> endpoint requires Pro plan or higher.</p>
        </div>

        {/* Endpoint Tabs */}
        <div className="mb-4 flex gap-1 border-b border-slate-200" data-testid="endpoint-tabs">
          {Object.entries(endpoints).map(([key, ep]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className={`font-mono text-xs mr-1.5 ${activeTab === key ? 'text-blue-500' : 'text-slate-400'}`}>{ep.method}</span>
              {ep.path}
            </button>
          ))}
        </div>

        {/* Active Endpoint */}
        <div className="space-y-6" data-testid="endpoint-detail">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 text-xs font-mono font-bold ${active.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
              {active.method}
            </span>
            <code className="text-lg font-mono text-slate-950">{active.path}</code>
          </div>
          <p className="text-sm text-slate-600">{active.description}</p>

          {/* Parameters */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Parameters</h3>
            <div className="bg-white border border-slate-200">
              {active.params.map((p) => (
                <div key={p.name} className="flex items-start gap-4 px-4 py-3 border-b border-slate-100 last:border-0">
                  <code className="font-mono text-sm text-slate-950 font-semibold min-w-[100px]">{p.name}</code>
                  <span className="text-xs font-mono text-slate-500 min-w-[60px]">{p.type}</span>
                  {p.required && <span className="text-xs font-mono text-red-500">required</span>}
                  <span className="text-sm text-slate-600">{p.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Example Request */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Example Request</h3>
            <CodeBlock code={active.curl} />
          </div>

          {/* Example Response */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Example Response</h3>
            <CodeBlock code={active.response} />
          </div>
        </div>

        {/* Error Codes */}
        <div className="mt-12 bg-white border border-slate-200 p-6" data-testid="error-codes">
          <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Error Codes</h2>
          <div className="space-y-2">
            {[
              ['401', 'Missing, invalid, or revoked API key'],
              ['400', 'Invalid request parameters (bad location, date, etc.)'],
              ['403', 'Feature unavailable (e.g., report generation in limited mode)'],
              ['429', 'Rate limit exceeded'],
              ['500', 'Internal server error'],
            ].map(([code, desc]) => (
              <div key={code} className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0">
                <code className="font-mono text-sm font-bold text-slate-950 min-w-[40px]">{code}</code>
                <span className="text-sm text-slate-600">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApiDocs;
